import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiError } from "@/lib/apiClient";
import { ItineraryEditor } from "./ItineraryEditor";
import CustomersManager from "./CustomersManager";
import { SITE } from "@/data/site";
import { toast } from "sonner";
import {
  LayoutDashboard, Map, Inbox, PartyPopper, Users, LogOut, Plus, Pencil, Trash2,
  Eye, EyeOff, Phone, Mail, ExternalLink, MapPin, Newspaper, Sparkles, AtSign, FileText, Star,
} from "lucide-react";
import { DestinationsManager, JournalManager, StoriesManager, NewsletterManager, ContentManager, ReviewsManager } from "./AdminContent";

const STATUSES = ["new", "contacted", "quoted", "booked", "closed"];
const statusColor = {
  new: "bg-[#F49E1A]/15 text-[#B4740C]", contacted: "bg-blue-100 text-blue-700",
  quoted: "bg-purple-100 text-purple-700", booked: "bg-green-100 text-green-700",
  closed: "bg-gray-200 text-gray-600",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const isAdmin = user?.role === "admin";

  const nav = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "content", label: "Site Content", icon: FileText },
    { id: "itineraries", label: "Journeys", icon: Map },
    { id: "destinations", label: "Destinations", icon: MapPin },
    { id: "leads", label: "Leads", icon: Inbox },
    { id: "customers", label: "Customers", icon: Users },
    { id: "events", label: "Event Enquiries", icon: PartyPopper },
    { id: "reviews", label: "Google Reviews", icon: Star },
    { id: "journal", label: "Journal", icon: Newspaper },
    { id: "stories", label: "Stories", icon: Sparkles },
    { id: "newsletter", label: "Newsletter", icon: AtSign },
    ...(isAdmin ? [{ id: "staff", label: "Staff", icon: Users }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#FFF7EC] flex">
      {/* Sidebar */}
      <aside className="w-16 sm:w-60 bg-[#361E02] text-[#F9EFE1] flex flex-col shrink-0">
        <div className="p-4 sm:p-6 border-b border-white/10">
          <img src={SITE.logo} alt="VacanzaGo" className="h-8 bg-white rounded p-1 hidden sm:block" />
          <span className="sm:hidden font-display text-xl text-[#F49E1A]">VG</span>
        </div>
        <nav className="flex-1 p-2 sm:p-4 space-y-1">
          {nav.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${tab === n.id ? "bg-[#F49E1A] text-white" : "text-[#F9EFE1]/70 hover:bg-white/5"}`}>
              <n.icon size={18} /> <span className="hidden sm:inline">{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-2 sm:p-4 border-t border-white/10">
          <div className="hidden sm:block px-3 pb-2 text-xs text-[#F9EFE1]/50">{user?.name} · {user?.role}</div>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-[#F9EFE1]/70 hover:bg-white/5"><LogOut size={18} /> <span className="hidden sm:inline">Sign out</span></button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-10 overflow-x-hidden">
        {tab === "overview" && <Overview />}
        {tab === "content" && <ContentManager />}
        {tab === "itineraries" && <Itineraries />}
        {tab === "destinations" && <DestinationsManager />}
        {tab === "leads" && <Leads source={null} title="All leads" testid="leads" />}
      {tab === "customers" && <CustomersManager />}
        {tab === "events" && <Leads source="event" title="Event enquiries" testid="events" />}
        {tab === "reviews" && <ReviewsManager />}
        {tab === "journal" && <JournalManager />}
        {tab === "stories" && <StoriesManager />}
        {tab === "newsletter" && <NewsletterManager />}
        {tab === "staff" && isAdmin && <Staff currentEmail={user.email} />}
      </main>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const cards = [
    { l: "Total journeys", v: stats?.itineraries, k: "itineraries" },
    { l: "Published", v: stats?.published, k: "published" },
    { l: "Total leads", v: stats?.leads, k: "leads" },
    { l: "New leads", v: stats?.new_leads, k: "new" },
    { l: "Event enquiries", v: stats?.event_leads, k: "events" },
  ];
  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1808] mb-8">Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.k} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-6" data-testid={`stat-${c.k}`}>
            <div className="font-display text-4xl font-semibold text-[#2A1808]">{c.v ?? "—"}</div>
            <div className="mt-1 text-xs text-[#8A6B47]">{c.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Itineraries() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/itineraries").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (slug) => {
    if (!window.confirm("Delete this journey?")) return;
    try { await api.delete(`/itineraries/${slug}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async (it) => {
    try { await api.put(`/itineraries/${it.slug}`, { ...it, published: !it.published }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-[#2A1808]">Journeys</h1>
        <button onClick={() => setCreating(true)} data-testid="admin-add-itinerary" className="inline-flex items-center gap-2 rounded-full btn-orange px-5 py-2.5 text-sm"><Plus size={16} /> New journey</button>
      </div>
      <div className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9EFE1] text-[#553204] text-left">
            <tr><th className="p-3 font-semibold">Title</th><th className="p-3 font-semibold hidden sm:table-cell">Region</th><th className="p-3 font-semibold hidden md:table-cell">From</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.slug} className="border-t border-[#553204]/8" data-testid={`admin-itinerary-row-${it.slug}`}>
                <td className="p-3 text-[#2A1808] font-medium">{it.title}</td>
                <td className="p-3 text-[#8A6B47] hidden sm:table-cell">{it.region}</td>
                <td className="p-3 text-[#8A6B47] hidden md:table-cell">{inr(it.price_from)}</td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${it.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{it.published ? "Published" : "Draft"}</span></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => togglePublish(it)} title="Toggle publish" className="p-2 text-[#8A6B47] hover:text-[#F49E1A]" data-testid={`toggle-publish-${it.slug}`}>{it.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                    <button onClick={() => setEditing(it)} title="Edit" className="p-2 text-[#8A6B47] hover:text-[#F49E1A]" data-testid={`edit-${it.slug}`}><Pencil size={16} /></button>
                    <button onClick={() => del(it.slug)} title="Delete" className="p-2 text-[#8A6B47] hover:text-red-500" data-testid={`delete-${it.slug}`}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(editing || creating) && (
        <ItineraryEditor initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function Leads({ source, title, testid }) {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    api.get("/leads", { params: { source: source || undefined, status: filter || undefined } }).then((r) => setLeads(r.data)).catch(() => {});
  }, [source, filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/leads/${id}`, { status }); load(); } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete lead?")) return; await api.delete(`/leads/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl text-[#2A1808]">{title}</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`${testid}-status-filter`} className="rounded-full border border-[#553204]/15 bg-white px-4 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>
      {leads.length === 0 ? <p className="text-[#8A6B47]" data-testid={`${testid}-empty`}>No leads yet.</p> : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-5" data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-[#2A1808]">{l.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[l.status]}`}>{l.status}</span>
                    <span className="text-xs text-[#8A6B47] capitalize">· {l.source}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-[#8A6B47] flex-wrap">
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-[#F49E1A]"><Phone size={13} /> {l.phone}</a>}
                    {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[#F49E1A]"><Mail size={13} /> {l.email}</a>}
                    {l.itinerary_slug && <a href={`/packages/${l.itinerary_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#F49E1A]"><ExternalLink size={13} /> {l.itinerary_slug}</a>}
                  </div>
                  {l.message && <p className="mt-2 text-sm text-[#553204]">{l.message}</p>}
                  {l.payload && Object.keys(l.payload).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(l.payload).filter(([, v]) => v).map(([k, v]) => (
                        <span key={k} className="text-xs bg-[#F9EFE1] border border-[#553204]/10 rounded px-2 py-0.5 text-[#553204]">{k}: {String(v)}</span>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#8A6B47]/70">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="rounded-md border border-[#553204]/15 bg-white px-2 py-1.5 text-xs capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => del(l.id)} className="p-2 text-[#8A6B47] hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Staff({ currentEmail }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("All fields required"); return; }
    setBusy(true);
    try { await api.post("/staff", form); toast.success("Staff added"); setForm({ name: "", email: "", password: "", role: "staff" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove this staff member?")) return; try { await api.delete(`/staff/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const input = "w-full rounded-md border border-[#553204]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#F49E1A]";

  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1808] mb-8">Staff & permissions</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <form onSubmit={create} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-6 space-y-3" data-testid="staff-form">
          <h2 className="font-display text-2xl text-[#2A1808]">Add staff</h2>
          <input className={input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
          <input className={input} placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
          <input className={input} placeholder="Password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
          <select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
            <option value="staff">Staff (journeys & leads)</option>
            <option value="admin">Admin (full access)</option>
          </select>
          <button disabled={busy} className="w-full rounded-full btn-orange px-5 py-2.5 text-sm disabled:opacity-60" data-testid="staff-submit">{busy ? "Adding…" : "Add staff"}</button>
        </form>
        <div className="lg:col-span-2 space-y-3">
          {staff.map((s) => (
            <div key={s.id} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4 flex items-center justify-between" data-testid={`staff-row-${s.email}`}>
              <div>
                <p className="font-medium text-[#2A1808]">{s.name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${s.role === "admin" ? "bg-[#F49E1A]/15 text-[#B4740C]" : "bg-gray-200 text-gray-600"}`}>{s.role}</span></p>
                <p className="text-sm text-[#8A6B47]">{s.email}</p>
              </div>
              {s.email !== currentEmail && s.email !== "swikriti140@gmail.com" && (
                <button onClick={() => del(s.id)} className="p-2 text-[#8A6B47] hover:text-red-500" data-testid={`staff-delete-${s.email}`}><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
